export type ClipStatus =
  | "needs_processing"
  | "queued_for_enrichment"
  | "enriched"
  | "deprecated"
  | "missing"

export const CLIP_STATUSES: ClipStatus[] = [
  "needs_processing",
  "queued_for_enrichment",
  "enriched",
  "deprecated",
  "missing",
]

export const CLIP_STATUS_LABELS: Record<ClipStatus, string> = {
  needs_processing:      "Needs processing",
  queued_for_enrichment: "Queued for enrichment",
  enriched:              "Enriched",
  deprecated:            "Deprecated",
  missing:               "Missing",
}

export type ClipThumbnail = {
  id: number
  position: number
  frame_timestamp_seconds: number
  is_primary: boolean
  url: string | null
}

export type ClipTypeRef = { id: number; name: string }

// Full clip type record, as managed in Settings → Clip types.
export type ClipTypeState = {
  id: number
  name: string
  folder_path: string
  inbox_folder_path: string
  clip_count: number
  created_at: string
  updated_at: string
}

export type Clip = {
  id: number
  dropbox_file_id: string
  title: string | null
  short_description: string | null
  status: ClipStatus
  one_time_use: boolean
  clip_type: ClipTypeRef | null
  current_dropbox_path: string | null
  current_folder_path: string | null
  current_filename: string | null
  dropbox_web_url: string | null
  duration_seconds: number | null
  recorded_date: string | null
  enriched_at: string | null
  enriched_by_agent: boolean
  agent_reviewed_at: string | null
  low_confidence_enrichment: boolean
  tag_names: string[]
  auto_match_triggers: string[]
  thumbnails: ClipThumbnail[]
  primary_thumbnail_url: string | null
  has_thumbnails: boolean
  created_at: string
  updated_at: string
}

export type ClipEvent = {
  id: number
  event_type: string
  body: string | null
  metadata: Record<string, unknown>
  author_type: "user" | "agent" | "system"
  author_identifier: string
  created_at: string
}

export type ClipDetail = Clip & {
  long_description: string | null
  events: ClipEvent[]
  events_comment_count: number
}

export type TagRef = { id: number; name: string }

export type DropboxIntegrationState = {
  connected: boolean
  configured: boolean
  account_email: string | null
  broll_root_path: string | null
  deprecated_folder_names: string[]
  default_deprecated_folder_name: string
  ignored_folder_names: string[]
  last_synced_at: string | null
  last_sync_outcome: "succeeded" | "errored" | "skipped" | null
  last_sync_details: Record<string, unknown> | null
}

export type SyncStatus = {
  outcome: "succeeded" | "errored" | "skipped" | null
  started_at: string | null
  completed_at: string | null
  details: Record<string, unknown> | null
} | null

export type ClipFilters = {
  status: string
  tag_id: string | number | null
  q: string
  folder_path: string | null
  // A ClipType id, or the literal "none" for clips without a type.
  clip_type_id: string | number | null
}

export type ClipSortKey =
  | "recently_recorded"
  | "recently_synced"
  | "title"
  | "folder"
  | "duration"
  | "recorded"
  | "status"

export type ClipSortDirection = "asc" | "desc"

// Columns the list-view header lets the user click to sort by.
export type ClipSortableColumn = "title" | "folder" | "duration" | "recorded" | "status"

export const COLUMN_DEFAULT_DIRECTION: Record<ClipSortableColumn, ClipSortDirection> = {
  title:    "asc",
  folder:   "asc",
  status:   "asc",
  duration: "desc",
  recorded: "desc",
}

export type ClipView = "grid" | "list"
